import { useState, useEffect } from "react";
import { useInput } from "../../hooks/use-input";
import { useThunk } from "../../hooks/useThunk";

import CustomButton from "../../components/Buttons/CustomButton";
import Input from "../../components/Form/Input";
import {
  getBreachTemplate,
  sendBreachEmail,
} from "../../services/thunks/breachThunks";
import showToast from "../../utils/toastNotifications";

import {
  validateToField,
  validateMultipleEmails,
} from "../../utils/validationUtils";
import {LoaderCircle} from "lucide-react"
import EmailSendsPopup from "../../components/Modals/ConfirmSendModal";

const BreachNotification = () => {

  // No-op validation function for fields that don't need validation
  const noValidation = () => "";

  const {
    value: emailTo,
    handleInputChange: onToChange,
    handleInputBlur: onToBlur,
    setValue: setTo,
    hasError: toHasError,
    errorMessage: toErrorMessage,
    reset: resetTo,
  } = useInput("", validateToField);

  const {
    value: emailBCC,
    handleInputChange: onBCCChange,
    handleInputBlur: onBCCBlur,
    setValue: setBCC,
    hasError: BCCHasError,
    errorMessage: BCCErrorMessage,
    reset: resetBCC,
  } = useInput("", validateMultipleEmails);

  const {
    value: emailSubject,
    handleInputChange: onSubjectChange,
    handleInputBlur: onSubjectBlur,
    setValue: setSubject,
    reset: resetSubject,
  } = useInput("", noValidation);

  const {
    value: emailMessage,
    handleInputChange: onMessageChange,
    handleInputBlur: onMessageBlur,
    setValue: setMessage,
    reset: resetMessage,
  } = useInput("", noValidation);

  const [doFetchTemplate, fetchingTemplate] = useThunk(getBreachTemplate);
  const [sendEmail, sendingEmail] = useThunk(sendBreachEmail);
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  useEffect(() => {
    const getTemplate = async () => {
      const res = await doFetchTemplate();
      console.log("res", res);

      const template = res?.response;
      console.log("template", template);

      if (!template) return;
      setTo(template?.to || "");
      setSubject(template?.subject || "");
      setMessage(template?.message || "");
    };
    getTemplate();
  }, [doFetchTemplate]);

  const handleSendEmail = async () => {
    // Validate required fields
    if (!emailTo.trim() || !emailSubject.trim() || !emailMessage.trim()) {
      showToast("error", "Please fill all the fields");
      return;
    }

    // Check for validation errors in To field
    if (toHasError || BCCHasError) {
      showToast("error", "Please enter a valid email");
      return;
    }

    // Show confirmation popup
    setShowEmailPopup(true);
  };

  const handleConfirmSendEmail = async () => {
    const emailData = {
      to: emailTo.trim(),
      bcc: emailBCC.trim(),
      subject: emailSubject.trim(),
      message: emailMessage.trim(),
    };

    try {
      const res = await sendEmail(emailData);

      if (res?.success || res?.data) {
        showToast("success", "Email sent successfully!");
        setShowEmailPopup(false);

        // Reset form after successful send
        resetTo("");
        resetBCC("");
        resetSubject("");
        resetMessage("");
      } else {
        showToast("error", "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showToast("error", "Failed to send email");
    }
  };

  const handleClearForm = () => {
    resetTo("");
    resetBCC("");
    resetSubject("");
    resetMessage("");
  };

  return (
    <div className="min-h-screen px-6 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="rounded-2xl border border-[#1C2A36] bg-[#0a0f14] p-6 md:p-8">
          {/* To Field */}
          <Input
            id="to"
            name="to"
            label="To * "
            placeholder="Enter 'all' to send to all users, or email addresses (comma separated)"
            value={emailTo}
            onChange={onToChange}
            onBlur={onToBlur}
            error={toHasError}
            errorMessage={toHasError ? toErrorMessage : null}
            className="bg-[#101018] text-white"
            outerContainerStyle="mb-4"
          />

          {/* CC Field */}
          <Input
            id="bcc"
            name="bcc"
            label="BCC"
            placeholder="Enter BCC email(s) - comma separated for multiple"
            value={emailBCC}
            onChange={onBCCChange}
            onBlur={onBCCBlur}
            error={BCCHasError}
            errorMessage={BCCHasError ? BCCErrorMessage : null}
            className="bg-[#101018] text-white"
            outerContainerStyle="mb-4"
          />

          {/* Subject Field */}
          <Input
            id="subject"
            name="subject"
            label="Subject *"
            placeholder="Enter email subject"
            value={emailSubject}
            onChange={onSubjectChange}
            onBlur={onSubjectBlur}
            className="bg-[#101018] text-white"
            outerContainerStyle="mb-4"
          />

          {/* Email Body */}
          <Input
            id="body"
            name="body"
            label="Message *"
            type="textarea"
            placeholder="Enter your breach notification message here..."
            value={emailMessage}
            onChange={onMessageChange}
            onBlur={onMessageBlur}
            className="bg-[#101018] text-white resize-y h-96"
            outerContainerStyle="mb-6"
          />

          <div className="flex flex-wrap gap-4 justify-end">
            <CustomButton
              onClick={handleClearForm}
              disabled={sendingEmail}
              className="w-[100px] h-[45px] rounded-[5px] font-normal py-0 px-0 uppercase"
              rounded="rounded-[5px]"
              textColor="text-[#808989]"
              borderColor="border-[#808989]"
            >
              Clear
            </CustomButton>

            <CustomButton
              onClick={handleSendEmail}
              disabled={sendingEmail}
              useGradientBorder
              className="w-[136px] h-[41px] rounded-[5px] text-[18px] font-medium font-public-sans py-0 px-4"
              rounded="rounded-[5px]"
              textColor="gradient"
              gradient="bg-[linear-gradient(180deg,#51F8EF_0%,#1A7DD1_100%)]"
            >
              Send Email
            </CustomButton>
          </div>
        </div>
      </div>
      
      <EmailSendsPopup
        isOpen={showEmailPopup}
        onClose={() => setShowEmailPopup(false)}
        onConfirm={handleConfirmSendEmail}
        loading={sendingEmail}
      />
    </div>
  );
};

export default BreachNotification;
